// PerformanceMetricsPage.js
import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import Chart from 'react-apexcharts';
import { 
  Card, 
  CardContent, 
  Typography, 
  Grid, 
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  TableChart as TableChartIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Info as InfoIcon
} from '@mui/icons-material';

const PerformanceMetricsPage = () => {
  const [title, setTitle] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [metricsData, setMetricsData] = useState({});
  const [chartOptions, setChartOptions] = useState([]);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  // Function to format column names for better readability
  const formatColumnName = (columnName) => {
    if (!columnName) return '';
    
    // Handle common abbreviations and patterns
    const replacements = {
      'ID': 'ID',
      'SEL': 'Selection',
      'INS': 'Insert',
      'UPD': 'Update',
      'DEL': 'Delete',
      'Created': 'Created',
      'Edited': 'Edited',
      'Records': 'Records',
      'NumCalls': 'Number of Calls',
      'DaysBack': 'Days Back',
      'ClientID': 'Client ID',
      'UserID': 'User ID',
      'ClientName': 'Client Name',
      'UserName': 'User Name'
    };
    
    // Split by camelCase and capitalize
    let formatted = columnName.replace(/([A-Z])/g, ' $1').trim();
    
    // Apply specific replacements
    Object.entries(replacements).forEach(([key, value]) => {
      formatted = formatted.replace(new RegExp(key, 'g'), value);
    });
    
    // Clean up extra spaces and capitalize first letter
    formatted = formatted.replace(/\s+/g, ' ').trim();
    formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);
    
    return formatted;
  };

  useEffect(() => {
    if (window.innerWidth < 767) {
      setIsMobile(true);
    }
    
    window.addEventListener('resize', (event) => {
      if (event.target.innerWidth < 767) {
        setIsMobile(true);
      } else {
        setIsMobile(false);
      }
    }, true);

    // Allow page scroll (index.css sets overflow:hidden + position:fixed on html/body for the map)
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    document.body.style.height = 'auto';
    document.documentElement.style.overflow = 'auto';
    document.documentElement.style.position = 'static';
    document.documentElement.style.height = 'auto';
    
    // Set body background to white with multiple approaches for better coverage
    document.body.style.backgroundColor = 'white';
    document.body.style.background = 'white';
    document.body.style.setProperty('background-color', 'white', 'important');
    document.body.style.setProperty('background', 'white', 'important');
    
    // Also set html element background
    document.documentElement.style.backgroundColor = 'white';
    document.documentElement.style.background = 'white';
    
    // Get items from local storage
    const storedMetricsData = JSON.parse(localStorage.getItem('performanceMetricsData')) || {};
    const storedChartOptions = JSON.parse(localStorage.getItem('performanceChartOptions')) || [];
    const storedTitle = localStorage.getItem('performanceMetricsTitle');
    
    setTitle(storedTitle || 'Performance Metrics Report');
    setMetricsData(storedMetricsData);
    setChartOptions(storedChartOptions);
    
    // Cleanup: restore global scroll lock and background when leaving this route
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.height = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.position = '';
      document.documentElement.style.height = '';
      document.body.style.backgroundColor = '';
      document.body.style.background = '';
      document.body.style.removeProperty('background-color');
      document.body.style.removeProperty('background');
      document.documentElement.style.backgroundColor = '';
      document.documentElement.style.background = '';
      
      // Clean up localStorage
      localStorage.removeItem('performanceMetricsData');
      localStorage.removeItem('performanceChartOptions');
      localStorage.removeItem('performanceMetricsTitle');
      localStorage.removeItem('performanceMetricsMetadata');
    };
  }, []);

  const renderMetricsTable = (metricName, data) => {
    if (!data || data.length === 0) {
      return (
        <Card 
          sx={{ 
            mb: 3, 
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TableChartIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="h6" color="text.secondary">
                {metricName} - No data available
              </Typography>
            </Box>
          </CardContent>
        </Card>
      );
    }

    const columns = Object.keys(data[0]);
    
    // Try to get a more readable name for the metric
    let readableMetricName = metricName;
    try {
      // Check if we have stored performance metrics metadata
      const storedMetadata = localStorage.getItem('performanceMetricsMetadata');
      if (storedMetadata) {
        const metadata = JSON.parse(storedMetadata);
        const metricObj = metadata.find(sp => sp.name === metricName);
        if (metricObj && metricObj.displayName) {
          readableMetricName = metricObj.displayName;
        }
      }
    } catch (error) {
      console.log('Error parsing performance metrics metadata:', error);
    }
    
    return (
      <Card 
        sx={{ 
          mb: 3, 
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            p: 3, 
            pb: 2, 
            backgroundColor: theme.palette.primary.main, 
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TableChartIcon sx={{ mr: 1.5 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {readableMetricName}
              </Typography>
            </Box>
            <Chip 
              label={`${data.length} record${data.length !== 1 ? 's' : ''}`}
              size="small"
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                fontWeight: 500
              }}
            />
          </Box>
          
          <TableContainer component={Paper} sx={{ maxHeight: 500, borderRadius: 0 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell 
                      key={column} 
                      sx={{ 
                        fontWeight: 700,
                        backgroundColor: theme.palette.grey[50],
                        color: theme.palette.text.primary,
                        fontSize: '0.875rem',
                        '&:first-of-type': { pl: 3 },
                        '&:last-of-type': { pr: 3 }
                      }}
                    >
                      {formatColumnName(column)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row, index) => (
                  <TableRow 
                    key={index}
                    sx={{ 
                      '&:nth-of-type(even)': { backgroundColor: theme.palette.grey[25] },
                      '&:hover': { backgroundColor: theme.palette.action.hover }
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell 
                        key={column}
                        sx={{ 
                          fontSize: '0.875rem',
                          '&:first-of-type': { pl: 3 },
                          '&:last-of-type': { pr: 3 }
                        }}
                      >
                        {row[column] !== null && row[column] !== undefined ? row[column].toString() : '-'}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };

  const renderChart = (chartOption, index) => {
    return (
      <Grid item xs={12} lg={6} key={index}>
        <Card 
          sx={{ 
            height: '100%', 
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden'
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ 
              p: 2.5, 
              backgroundColor: theme.palette.secondary.main, 
              color: 'white',
              display: 'flex',
              alignItems: 'center'
            }}>
              <BarChartIcon sx={{ mr: 1.5 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {chartOption.options.title?.text || 'Chart'}
              </Typography>
            </Box>
            <Box sx={{ p: 2.5 }}>
              <Chart 
                options={{
                  ...chartOption.options,
                  chart: {
                    ...chartOption.options.chart,
                    toolbar: {
                      show: true,
                      tools: {
                        download: true,
                        selection: false,
                        zoom: true,
                        zoomin: true,
                        zoomout: true,
                        pan: true,
                        reset: true
                      }
                    },
                    animations: {
                      enabled: true,
                      easing: 'easeinout',
                      speed: 800
                    }
                  },
                  colors: [theme.palette.primary.main, theme.palette.secondary.main],
                  grid: {
                    borderColor: theme.palette.divider,
                    strokeDashArray: 3
                  },
                  tooltip: {
                    theme: 'light',
                    style: {
                      fontSize: '12px'
                    }
                  }
                }} 
                series={chartOption.series} 
                type="bar" 
                height={350}
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const renderSummaryCards = () => {
    const totalMetrics = Object.keys(metricsData).length;
    const totalCharts = chartOptions.length;
    const totalRecords = Object.values(metricsData).reduce((sum, data) => sum + (data?.length || 0), 0);

    const summaryItems = [
      { label: 'Total Metrics', value: totalMetrics, icon: AssessmentIcon, color: 'primary' },
      { label: 'Charts Generated', value: totalCharts, icon: BarChartIcon, color: 'secondary' },
      { label: 'Data Records', value: totalRecords, icon: TrendingUpIcon, color: 'success' }
    ];

    return (
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3}>
          {summaryItems.map((item, index) => (
            <Grid item xs={12} sm={4} key={index}>
              <Card 
                sx={{ 
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  border: '1px solid',
                  borderColor: 'divider',
                  textAlign: 'center',
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  mb: 2,
                  color: `${item.color}.main`
                }}>
                  <item.icon sx={{ fontSize: 40 }} />
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 700, mb: 1 }}>
                  {item.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.label}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: theme.palette.grey[25],
      py: 4,
      px: isSmallScreen ? 2 : 4
    }}>
      {/* Header Section */}
      <Box sx={{ 
        textAlign: 'center', 
        mb: 4,
        backgroundColor: 'white',
        borderRadius: 3,
        p: 4,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        border: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <AssessmentIcon sx={{ fontSize: 48, color: 'primary.main', mr: 2 }} />
        </Box>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
          {title}
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          Comprehensive performance analytics and insights for your city services operations
        </Typography>
      </Box>

      {/* Summary Cards */}
      {(Object.keys(metricsData).length > 0 || chartOptions.length > 0) && renderSummaryCards()}
      
      {/* Charts Section */}
      {chartOptions.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 3,
            backgroundColor: 'white',
            borderRadius: 2,
            p: 2.5,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <BarChartIcon sx={{ mr: 2, color: 'secondary.main' }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Performance Charts
            </Typography>
            <Tooltip title="Interactive charts showing key performance indicators">
              <IconButton size="small" sx={{ ml: 1 }}>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Grid container spacing={3}>
            {chartOptions.map((chartOption, index) => 
              renderChart(chartOption, index)
            )}
          </Grid>
        </Box>
      )}
      
      {/* Metrics Tables Section */}
      {Object.keys(metricsData).length > 0 && (
        <Box>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 3,
            backgroundColor: 'white',
            borderRadius: 2,
            p: 2.5,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <TableChartIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Performance Data
            </Typography>
            <Tooltip title="Detailed tabular data for each performance metric">
              <IconButton size="small" sx={{ ml: 1 }}>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {Object.entries(metricsData).map(([metricName, data]) => 
            renderMetricsTable(metricName, data)
          )}
        </Box>
      )}
      
      {/* Empty State */}
      {Object.keys(metricsData).length === 0 && chartOptions.length === 0 && (
        <Box sx={{ 
          textAlign: 'center', 
          mt: 8,
          backgroundColor: 'white',
          borderRadius: 3,
          p: 6,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          border: '1px solid',
          borderColor: 'divider'
        }}>
          <AssessmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" color="text.secondary" gutterBottom>
            No Performance Metrics Available
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Run performance metrics from the Create Report modal to view data and charts here.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PerformanceMetricsPage;
